/**
 * The number pad: the app's own, for every number typed — a shot's elevation on the plan, and every
 * field (2026-10-24, Ricky: on the standard iPad the keyboard's numbers are the second function of
 * its top row, swiped down for every digit). Large keys in the order of the iPad's own number pad
 * (1 2 3 on top), Delete at the top right, Return tall under the thumb, and beside it the key the
 * field needs: the keyboard (ABC) where letters may be typed too, a sign, a station's plus.
 *
 * Glass like every control. Carried by its grip anywhere over the screen, it settles against the
 * edge it is let go near; where it opens is for whoever opens it to choose (`spotFor`). A key acts
 * as the finger comes down on it — the pad keeps up with a thumb typing fast — and a touch on it is
 * never a touch on what lies under it, nor takes the caret from the field being typed into.
 */
import { h, haptic, icon, ICONS } from './dom.ts';
import { kickGlass } from './glass.ts';

export type PadKey = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '.' | 'sign' | 'plus' | 'minus' | 'back' | 'clear';
/** The key beside Return: the keyboard (letters may be typed too), a sign, a station's plus, a minus, Clear — or none. */
export type PadExtra = 'abc' | 'sign' | 'plus' | 'minus' | 'clear' | null;
export interface PadLayout {
  extra: PadExtra;
  /** A point may be typed (not into a count). */
  point: boolean;
  /** Return finishes (the last field, the shot) or goes on to the next field. */
  enter: 'done' | 'next';
  /** The keyboard is up: the ABC key puts it away. */
  keyboard: boolean;
}
export interface Vec {
  x: number;
  y: number;
}
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}
/** Where a pad was let go: its top-left (client px), and the edge it settled against, if any. */
export interface PadSpot extends Vec {
  dock: 'left' | 'right' | null;
}
export interface Numpad {
  readonly el: HTMLElement;
  readonly shown: boolean;
  /** Its top-left as shown (client px). */
  readonly at: Vec | null;
  setLayout(l: PadLayout): void;
  /** The pad's own display (a shot's: whose it is, and what is typed): for a field, none — the field shows what is typed. */
  display(d: { label: string[]; value: string; placeholder: string } | null): void;
  value(v: string): void;
  /** Smaller keys, where the screen is short (a keyboard up beside it, an iPad on its side). */
  compact(on: boolean): void;
  size(): { w: number; h: number };
  /**
   * Shown at a place (its top-left, client px), coming in from a side if it was not up. `trail`: it
   * comes with something moving (a sheet moving aside for it) — from that far back, as that moves.
   */
  show(at: Vec, from?: 'left' | 'right' | 'below', trail?: Vec): void;
  moveTo(at: Vec, animate?: boolean): void;
  hide(): void;
}

const EDGE_SNAP = 56;
const MARGIN = 12;
/** How the pad moves: a sheet's own motion (app.css `--spring`, a sheet moving aside in 0.45 s), so the two go as one. */
const GLIDE = { duration: 450, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' };
const still = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

export function createNumpad(on: { label: string; key: (k: PadKey) => void; enter: () => void; abc?: () => void; moved?: (s: PadSpot) => void; withDisplay?: boolean }): Numpad {
  const press = (k: PadKey) => {
    haptic();
    on.key(k);
  };
  let timer = 0;
  // Delete held down deletes on, quicker and quicker
  const repeat = (b: HTMLElement) => {
    const stop = () => {
      clearTimeout(timer);
      for (const t of ['pointerup', 'pointercancel', 'pointerleave']) b.removeEventListener(t, stop);
    };
    let wait = 420;
    const tick = () => {
      press('back');
      wait = Math.max(60, wait * 0.7);
      timer = window.setTimeout(tick, wait);
    };
    clearTimeout(timer);
    timer = window.setTimeout(tick, wait);
    for (const t of ['pointerup', 'pointercancel', 'pointerleave']) b.addEventListener(t, stop);
  };
  const key = (k: PadKey, label: Node | string, aria: string, cls: string) => {
    const b = h('button', { class: `pad-key ${cls}`, type: 'button', 'aria-label': aria }, label);
    b.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      press(k);
      if (k === 'back') repeat(b);
    });
    // (a click with no finger before it: Switch Control, a keyboard's Space)
    b.addEventListener('click', (e) => {
      if (e.detail === 0) press(k);
    });
    return b;
  };
  // the extra key acts as what it now is
  let extraKey: PadKey | 'abc' | null = null;
  const digits = (['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'] as const).map((d) => key(d, d, d, `k-${d}`));
  const point = key('.', '.', 'Point', 'k-point pad-alt');
  const back = key('back', icon(ICONS.backspace, 24), 'Delete', 'k-back pad-alt');
  // the field's own key, and Return: they act on the click (a lifted finger), so that the keyboard
  // may come up for them, or the next field take the caret
  const extra = h('button', { class: 'pad-key k-extra pad-alt', type: 'button' });
  extra.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    if (extraKey && extraKey !== 'abc') press(extraKey);
  });
  extra.addEventListener('click', (e) => {
    if (extraKey === 'abc') {
      haptic();
      on.abc?.();
    } else if (extraKey && e.detail === 0) press(extraKey);
  });
  const enter = h('button', { class: 'pad-key k-enter', type: 'button' });
  enter.addEventListener('pointerdown', (e) => e.preventDefault());
  enter.addEventListener('click', () => {
    haptic();
    on.enter();
  });
  const keys = h('div', { class: 'pad-keys' }, ...digits, point, back, extra, enter);

  // the display (a shot's): whose, and what is typed, large; a clear button beside it
  const label = h('div', { class: 'pad-label' });
  const out = h('span', { class: 'pad-v' });
  const clear = h('button', { class: 'pad-clear', type: 'button', 'aria-label': 'Clear', onClick: () => press('clear') }, icon(ICONS.close, 14));
  const display = h('div', { class: 'pad-display', 'aria-live': 'polite', hidden: !on.withDisplay }, label, h('div', { class: 'pad-value' }, out, clear));
  let placeholder = '';
  const paint = (v: string) => {
    out.textContent = v || placeholder;
    out.classList.toggle('empty', !v);
    clear.hidden = !v;
  };

  const grip = h('div', { class: 'pad-grip', 'aria-hidden': 'true' }, h('span'));
  const el = h('div', { class: 'glass numpad', role: 'group', 'aria-label': on.label, hidden: true }, grip, display, keys);
  // a touch on the pad is the pad's: never a tap on the plan under it, never a pan, never the caret taken from the field
  for (const t of ['pointerdown', 'touchstart']) el.addEventListener(t, (e) => e.stopPropagation(), { passive: true });
  el.addEventListener('pointerdown', (e) => {
    if (e.target === el || e.target === keys) e.preventDefault();
  });
  el.addEventListener('mousedown', (e) => e.preventDefault());

  let shown = false;
  let at: Vec | null = null;
  const put = (p: Vec) => {
    at = { x: Math.round(p.x), y: Math.round(p.y) };
    el.style.translate = `${at.x}px ${at.y}px`;
  };
  // Its moves: one at a time, each from where the pad is drawn at that moment — a move begun while
  // another is under way goes on from there, never from where that one was going
  let glide: Animation | null = null;
  const drawnAt = (): Vec | null => {
    if (!at) return null;
    if (!glide || glide.playState !== 'running') return at;
    const [x, y] = getComputedStyle(el).translate.split(' ').map((v) => parseFloat(v));
    return Number.isFinite(x) && Number.isFinite(y) ? { x: x!, y: y! } : at;
  };
  const glideTo = (from: Vec, to: Vec) => {
    glide?.cancel();
    glide = el.animate([{ translate: `${from.x}px ${from.y}px` }, { translate: `${to.x}px ${to.y}px` }], GLIDE);
    // (its glass follows it across the plan, frame by frame)
    kickGlass();
  };
  const bounds = (): Rect => {
    const vv = window.visualViewport;
    return vv ? { x: vv.offsetLeft, y: vv.offsetTop, w: vv.width, h: vv.height } : { x: 0, y: 0, w: innerWidth, h: innerHeight };
  };
  const size = () => ({ w: el.offsetWidth, h: el.offsetHeight });
  const clampIn = (p: Vec): Vec => {
    const b = bounds();
    const s = size();
    return { x: Math.max(b.x + 4, Math.min(b.x + b.w - s.w - 4, p.x)), y: Math.max(b.y + 4, Math.min(b.y + b.h - s.h - 4, p.y)) };
  };

  // carried by its grip (or its display) anywhere; let go near an edge it settles against it
  const startDrag = (e: PointerEvent) => {
    if (!at || (e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    // (taken while it moves: held where it is drawn)
    const now = drawnAt();
    glide?.cancel();
    if (now) put(now);
    const target = e.currentTarget as HTMLElement;
    const dx = e.clientX - at.x,
      dy = e.clientY - at.y;
    target.setPointerCapture(e.pointerId);
    el.classList.add('carried');
    const move = (m: PointerEvent) => put(clampIn({ x: m.clientX - dx, y: m.clientY - dy }));
    const end = () => {
      target.removeEventListener('pointermove', move);
      target.removeEventListener('pointerup', end);
      target.removeEventListener('pointercancel', end);
      el.classList.remove('carried');
      if (!at) return;
      glide?.cancel();
      const b = bounds();
      const s = size();
      let dock: PadSpot['dock'] = null;
      let to = { ...at };
      if (at.x - b.x < EDGE_SNAP) {
        dock = 'left';
        to = { x: b.x + MARGIN, y: at.y };
      } else if (b.x + b.w - (at.x + s.w) < EDGE_SNAP) {
        dock = 'right';
        to = { x: b.x + b.w - s.w - MARGIN, y: at.y };
      }
      moveTo(to, true);
      on.moved?.({ ...to, dock });
    };
    target.addEventListener('pointermove', move);
    target.addEventListener('pointerup', end);
    target.addEventListener('pointercancel', end);
  };
  grip.addEventListener('pointerdown', startDrag);
  display.addEventListener('pointerdown', startDrag);

  const moveTo = (p: Vec, animate = false) => {
    const from = shown ? drawnAt() : null;
    put(p);
    if (animate && from && (Math.abs(from.x - at!.x) > 0.5 || Math.abs(from.y - at!.y) > 0.5) && !still()) glideTo(from, at!);
    else glide?.cancel();
  };

  return {
    el,
    get shown() {
      return shown;
    },
    get at() {
      return at;
    },
    setLayout(l) {
      extraKey = l.extra;
      extra.hidden = l.extra === null;
      extra.replaceChildren(
        l.extra === 'abc' ? (l.keyboard ? icon(ICONS.keyboardDown, 26) : h('span', { class: 'abc' }, 'ABC')) : l.extra === 'sign' ? '±' : l.extra === 'plus' ? '+' : l.extra === 'minus' ? '−' : l.extra === 'clear' ? h('span', { class: 'abc' }, 'C') : '',
      );
      extra.setAttribute('aria-label', l.extra === 'abc' ? (l.keyboard ? 'Hide keyboard' : 'Keyboard') : l.extra === 'sign' ? 'Minus sign' : l.extra === 'plus' ? 'Plus' : l.extra === 'minus' ? 'Minus' : 'Clear');
      point.hidden = !l.point;
      keys.classList.toggle('no-point', !l.point);
      keys.classList.toggle('no-extra', l.extra === null);
      enter.replaceChildren(l.enter === 'next' ? h('span', null, 'Next') : on.withDisplay ? h('span', { class: 'done' }, icon(ICONS.check, 20), h('span', null, 'Done')) : icon(ICONS.enterKey, 26));
      enter.setAttribute('aria-label', l.enter === 'next' ? 'Next field' : on.withDisplay ? 'Done' : 'Return');
      enter.classList.toggle('accent', l.enter === 'done');
    },
    display(d) {
      display.hidden = !d;
      if (!d) return;
      label.replaceChildren(...d.label.map((t, i) => h('span', { class: i ? 'pad-w' : 'pad-n' }, t)));
      placeholder = d.placeholder;
      paint(d.value);
      if (shown) display.animate([{ opacity: 0.4 }, { opacity: 1 }], { duration: 160, easing: 'ease-out' });
    },
    value: paint,
    compact(c) {
      el.classList.toggle('compact', c);
    },
    size() {
      const was = el.hidden;
      el.hidden = false;
      const s = size();
      el.hidden = was;
      return s;
    },
    show(p, from = 'right', trail) {
      el.hidden = false;
      el.style.pointerEvents = '';
      for (const a of el.getAnimations()) if (a !== glide) a.cancel();
      put(clampIn(p));
      if (shown) return;
      shown = true;
      if (still()) return;
      // it grows out of the side it comes from: a sheet's edge, or the screen's
      el.style.transformOrigin = from === 'left' ? 'left center' : from === 'below' ? 'center top' : 'right center';
      if (trail && (trail.x || trail.y)) {
        // with its sheet as it moves aside: from as far back, in the sheet's own motion
        glideTo({ x: at!.x + trail.x, y: at!.y + trail.y }, at!);
        el.animate([{ opacity: 0, scale: '0.92' }, { opacity: 1, scale: '1' }], GLIDE);
        return;
      }
      glide?.cancel();
      const off = from === 'left' ? '-24px 0' : from === 'below' ? '0 24px' : '24px 0';
      el.animate([{ opacity: 0, transform: `translate(${off}) scale(0.94)` }, { opacity: 1, transform: 'none' }], { duration: 340, easing: GLIDE.easing });
    },
    moveTo,
    hide() {
      if (!shown) return;
      shown = false;
      clearTimeout(timer);
      // (going, it takes no touch: one meant for the plan under it reaches the plan)
      el.style.pointerEvents = 'none';
      // (it goes from where it is drawn: a move under way stops there)
      const now = drawnAt();
      glide?.cancel();
      if (now) put(now);
      const a = el.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'scale(0.96)' }], { duration: 160, easing: 'cubic-bezier(0.4, 0, 1, 1)' });
      a.onfinish = () => {
        if (!shown) el.hidden = true;
      };
    },
  };
}

/**
 * Where a pad goes when it opens: where it was last put, if that covers nothing it must not;
 * else against the side of the screen away from what is being typed (the right first: the
 * thumb's), level with it; else the place covering least of it. `avoid`: what it must not cover
 * (the field, the tag being typed); `within`: the room it has.
 */
export function spotFor(size: { w: number; h: number }, avoid: readonly Rect[], within: Rect, kept: PadSpot | null, near: Rect | null): Vec {
  const fit = (p: Vec): Vec => ({ x: Math.max(within.x + 4, Math.min(within.x + within.w - size.w - 4, p.x)), y: Math.max(within.y + 4, Math.min(within.y + within.h - size.h - 4, p.y)) });
  const cost = (p: Vec): number => {
    let a = 0;
    for (const r of avoid) {
      const ow = Math.min(p.x + size.w, r.x + r.w + 12) - Math.max(p.x, r.x - 12);
      const oh = Math.min(p.y + size.h, r.y + r.h + 12) - Math.max(p.y, r.y - 12);
      if (ow > 0 && oh > 0) a += ow * oh;
    }
    return a;
  };
  const midY = near ? near.y + near.h / 2 - size.h / 2 : within.y + within.h / 2 - size.h / 2;
  const right = within.x + within.w - size.w - MARGIN;
  const left = within.x + MARGIN;
  const tries: Vec[] = [];
  if (kept) tries.push(kept.dock === 'right' ? { x: right, y: kept.y } : kept.dock === 'left' ? { x: left, y: kept.y } : kept);
  tries.push({ x: right, y: midY }, { x: left, y: midY }, { x: right, y: within.y + within.h / 2 - size.h / 2 }, { x: left, y: within.y + within.h / 2 - size.h / 2 });
  if (near) tries.push({ x: near.x + near.w + 16, y: midY }, { x: near.x - size.w - 16, y: midY }, { x: near.x + near.w / 2 - size.w / 2, y: near.y + near.h + 16 }, { x: near.x + near.w / 2 - size.w / 2, y: near.y - size.h - 16 });
  let best: { p: Vec; c: number } | null = null;
  for (const t of tries) {
    const p = fit(t);
    const c = cost(p);
    if (c === 0) return p;
    if (!best || c < best.c) best = { p, c };
  }
  return best!.p;
}
